import React, { ReactNode, useMemo } from 'react';
import styled from 'astroturf/react';
import classNames from 'classnames';
import { useRandomId } from 'js/utils/hooks/useRandomId';

import { Errors } from 'js/design_system/forms/Errors';

import 'app/assets/stylesheets/design_system/form.scss';
import _Header from "TextArea-Header.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/forms/TextArea.tsx?styleId=Header";


const Header = /*#__PURE__*/styled("div", null, {
  displayName: "Header",
  styles: _Header
});

export type TextAreaProps = Omit<
  React.HTMLProps<HTMLTextAreaElement>,
  'label' | 'onChange'
> & {
  label?: ReactNode;
  description?: ReactNode;
  limit?: number;
  /** Content to prepend before the textarea (e.g. a search icon) */
  prepend?: ReactNode;
  /** Content to append after the textarea */
  append?: ReactNode;
  errors?: ReactNode[];
  hasError?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
};

/**
 * # TextArea
 * A textarea component for multi-line text entry. Supports labels, descriptions, and error messages.
 *
 * Props:
 * - `label`: The textarea's label. Shown at the top of the component
 * - `description`: The textarea's description. Shown directly underneath the label
 * - `limit`: The maximum number of characters allowed in the textarea
 * - `prepend`: Content to prepend before the textarea (e.g. a search icon)
 * - `append`: Content to append after the textarea
 * - `hasError`: Whether to decorate the textarea with error styling (red border)
 * - `errors`: The errors to show beneath the textarea
 * - `onChange`: Change event for the textarea
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { TextArea } from 'js/design_system/forms/TextArea';
 *
 * <TextArea placeholder="Enter your name..." />
 * ```
 *
 * ## With Error
 * Avoid only using the `hasError` prop to indicate an error.
 * Color change should never be the only indicator of an error.
 * Pass the error message as a prop to the `errors` prop.
 *
 * @example
 *
 * ```tsx
 * import { TextArea } from 'js/design_system/forms/TextArea';
 *
 * <TextArea hasError errors={['Name cannot be blank']} />
 * ```
 *
 * ## With Character Limit
 * Bind the text area's value to state.
 * Handle any error produced by exceeding the character limit in the parent component.
 *
 * ```tsx
 * import { useState } from 'react';
 * import { TextArea } from 'js/design_system/forms/TextArea';
 *
 * const [value, setValue] = useState('');
 * const limit = 100;
 * const error = value.length > limit;
 *
 * <TextArea
 *  value={value}
 *  limit={100}
 *  hasError={error}
 *  errors={['Description cannot be more than 100 characters']}
 *  onChange={(event) => setValue(event.target.value)}
 * />
 * ```
 */
export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      value,
      label,
      description,
      limit,
      prepend,
      append,
      errors = [],
      hasError = false,
      disabled,
      className,
      ...props
    },
    ref,
  ) => {
    // Random ID for ARIA attributes
    const id = useRandomId();

    const characterCount = useMemo(() => {
      if (typeof value === 'string') return value.length;
      return 0;
    }, [value]);

    const hasSlots = prepend != null || append != null;

    const textAreaClasses = classNames(
      className,
      hasSlots ? 'ds2-5-text-area--slotted' : 'ds2-5-text-area',
      {
        'ds2-5-text-area--error': !disabled && hasError,
      },
    );

    const wrapperClasses = hasSlots
      ? classNames('ds2-5-text-area--wrapper', {
          'ds2-5-text-area--error': !disabled && hasError,
        })
      : undefined;

    const labelClasses = label
      ? classNames('ds2-5-form--label', {
          'ds2-5-form--label-disabled': disabled,
        })
      : undefined;

    const descriptionClasses = description
      ? classNames('ds2-5-form--desc', {
          'ds2-5-form--label-disabled': disabled,
        })
      : undefined;

    const limitClasses = limit
      ? classNames('ds2-5-text-area--limit', {
          'ds2-5-text-area--limit-disabled': disabled,
        })
      : undefined;

    const textareaElement = (
      <textarea
        id={`${id}-textarea`}
        className={textAreaClasses}
        ref={ref}
        aria-labelledby={label ? `${id}-label` : undefined}
        aria-describedby={description ? `${id}-desc` : undefined}
        aria-errormessage={!disabled && hasError ? `${id}-errors` : undefined}
        aria-invalid={!disabled && hasError}
        value={value}
        disabled={disabled}
        {...props}
      />
    );

    return (
      <div className="ds2-5-text-area--container">
        {(label || description || limit) && (
          <Header>
            <div>
              {label && (
                <label
                  htmlFor={`${id}-textarea`}
                  className={labelClasses}
                  id={`${id}-label`}
                >
                  {label}
                </label>
              )}
              {description && (
                <div className={descriptionClasses} id={`${id}-desc`}>
                  {description}
                </div>
              )}
            </div>
            {limit !== undefined && (
              <div className={limitClasses}>
                {characterCount.toLocaleString()}/{limit.toLocaleString()}
              </div>
            )}
          </Header>
        )}

        {hasSlots ? (
          <div className={wrapperClasses}>
            {prepend != null ? (
              <div className="ds2-5-text-area--prepend">{prepend}</div>
            ) : null}
            {textareaElement}
            {append != null ? (
              <div className="ds2-5-text-area--append">{append}</div>
            ) : null}
          </div>
        ) : (
          textareaElement
        )}

        {!disabled && hasError && (
          <Errors id={`${id}-errors`} errors={errors} />
        )}
      </div>
    );
  },
);
